import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppStore } from '@/stores/appStore';
import { Sidebar } from './Sidebar';
import { useScopeAutoSelect } from '@/hooks/useScopeAutoSelect';

interface ShellProps {
  children: React.ReactNode;
}

export function Shell({ children }: ShellProps) {
  const { mobileNavOpen, setMobileNavOpen } = useAppStore();
  const location = useLocation();
  useScopeAutoSelect();

  // The drawer closes on navigation and on Escape.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, setMobileNavOpen]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMobileNavOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileNavOpen, setMobileNavOpen]);

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-canvas">
      {/* Rail on md+ */}
      <div className="hidden md:flex">
        <Sidebar />
      </div>

      {/* Drawer below md */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setMobileNavOpen(false)}
            className="sfx-fade absolute inset-0 bg-[hsl(var(--sfx-brand-deep)/0.6)] backdrop-blur-sm"
          />
          <div className="sfx-drawer relative h-full w-72 max-w-[85vw] shadow-kumo-xl">
            <Sidebar forceExpanded />
          </div>
        </div>
      )}

      <main className="relative isolate flex min-w-0 flex-1 flex-col overflow-hidden bg-canvas">
        <div aria-hidden className="sfx-canvas-bloom -z-10" />
        {children}
      </main>
    </div>
  );
}
